import {isCurrency} from 'platform/components';
import {useCurrencySymbolFormatter, useFormatCurrency} from 'platform/locale';

import {isNil} from 'ramda';

import {BusinessCaseResponseBody} from '@dms/api/sales';
import {EMPTY_PLACEHOLDER} from '@dms/shared';

import {Nullish} from 'shared/web';

import {getTradeInCounterpartPrice} from '../../../utils/getTradeInCounterpartPrice';
import {getTradeInOffsetPrice} from '../../../utils/getTradeInOffsetPrice';

interface UseTradeInOffsetPricesParams {
  /** The linked trade-in counterpart BC; its agreed purchase price is the offset value. */
  counterpart: BusinessCaseResponseBody | Nullish;
  /** Base price the offset is calculated from — the host BC primary parameter. */
  baseAmount: string | Nullish;
  /** Currency to denominate prices in. */
  baseCurrency: string | Nullish;
}

/**
 * Formatted display values for the linked trade-in "offset" card:
 * - `formattedPrice` — the counterpart's agreed purchase price (the offset value).
 * - `formattedOffsetSource` — the base price after the offset is applied.
 *
 * When an amount is absent the row still shows the currency symbol ("– Kč" not "–"), so the empty
 * display is derived from the price currency (falling back to the base currency).
 */
export function useTradeInOffsetPrices(params: UseTradeInOffsetPricesParams) {
  const formatCurrency = useFormatCurrency();
  const formatSymbol = useCurrencySymbolFormatter();

  const counterpartPrice = getTradeInCounterpartPrice(params.counterpart);

  const offsetSource = getTradeInOffsetPrice({
    baseAmount: params.baseAmount,
    baseCurrency: params.baseCurrency,
    counterpart: params.counterpart,
  });

  const priceCurrency = counterpartPrice?.currency ?? params.baseCurrency;
  const priceSymbol = isCurrency(priceCurrency) ? formatSymbol(priceCurrency) : undefined;
  const emptyPriceDisplay = priceSymbol ? `${EMPTY_PLACEHOLDER}${priceSymbol}` : EMPTY_PLACEHOLDER;

  const formattedPrice = isNil(counterpartPrice)
    ? emptyPriceDisplay
    : (formatCurrency(
        Number(counterpartPrice.amount),
        counterpartPrice.currency ?? params.baseCurrency ?? ''
      ) ?? emptyPriceDisplay);

  const formattedOffsetSource = isNil(offsetSource)
    ? EMPTY_PLACEHOLDER
    : (formatCurrency(Number(offsetSource.amount), offsetSource.currency) ?? EMPTY_PLACEHOLDER);

  return {formattedPrice, formattedOffsetSource};
}
